import { FeatureToggles } from '@/features/feature-toggles'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { type Storage } from '@/infra/storage'
import { createObjectStorage, type ObjectStorage } from '@/infra/object-storage'
import {
  type ColumnId,
  JobsTableColumns,
  type StaticColumnDetails,
  type MutableColumnDetails,
} from './jobs-table-columns'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { isNotNil } from '@/utils/object-util'
import { move } from '@/utils/array-util'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { createJobsExportAdapter } from './jobs-export-adapter'
import { type ColumnDensity } from '@/components/Table/TableCell'
import { parseStoredColumnDensity } from './stored-job-list-values'
import type {
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetUserDataFn,
} from '@/features/iam'
import type { Id } from '@/infra/id'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Generates default mutable details for local storage.
 */
const defaultSettings: MutableColumnDetails[] = JobsTableColumns.map(
  (column) => {
    return {
      id: column.id,
      visible: true,
    }
  },
)

/**
 * The columns of the jobs table: which ones the user may see, their order,
 * visibility and density, and the CSV export of the jobs they show.
 */
export class JobsTableColumnsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Key used for storing column settings in local storage.
   * This key is for client and provider column settings.
   */
  private readonly _userKey = 'Jobs Table Column Settings - User'

  /**
   * Key used for storing column density setting in local storage.
   * This key is for client, provider, and admin column settings.
   */
  private readonly _densityKey = 'Jobs Table Density Setting - User'

  /**
   * Storage for column settings.
   */
  private readonly _storage: ObjectStorage

  private readonly _adapter

  /**
   * Columns to populate the jobs table header and rows with.
   */
  @observable accessor columns: (StaticColumnDetails & MutableColumnDetails)[] =
    []

  /**
   * Determines open state of column manager dropdown.
   */
  @observable accessor columnManagerOpen = false

  /**
   * The density of all columns in the table.
   */
  @observable accessor density: ColumnDensity = 'Default'

  constructor(
    storage: Storage,
    private readonly _getAllFilteredJobs: () => Promise<SearchableJob[]>,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getUserData: GetUserDataFn,
    private readonly _getSelectedWorkspaces: () => Id[],
    private readonly _keyPrefix: string,
  ) {
    this._storage = createObjectStorage(storage)
    this._adapter = createJobsExportAdapter(
      () => this._getAllFilteredJobs(),
      () => this.filteredColumns.filter((col) => !col.actionSelector),
      this._newAlert,
      this._createAlertErrorHandler,
    )
  }

  /**
   * Returns columns filtered by appropriate column types for user.
   */
  @computed
  get filteredColumns(): (StaticColumnDetails & MutableColumnDetails)[] {
    const currentMember = this._getCurrentMember()
    if (!currentMember) {
      return []
    }
    const canViewPayout = this._canViewPayoutColumn(currentMember.user.publicId)
    const canViewTotal = this._canViewTotalColumn(currentMember.user.publicId)
    const canViewIsRequestedPro = this._canViewIsRequestedProColumn()
    const canSelectJobsForActions = this.canSelectJobsForActions()
    const canViewProInfo = this._canViewProInfo()
    const canViewClientInfo = this._canViewClientInfo()

    return this.columns.filter((column) =>
      this._isColumnAccessible(
        column,
        canViewPayout,
        canViewTotal,
        canViewIsRequestedPro,
        canSelectJobsForActions,
        canViewClientInfo,
        canViewProInfo,
      ),
    )
  }

  /**
   * The IDs of the columns the table shows, in order.
   */
  @computed
  get visibleColumnIds(): ColumnId[] {
    return this.filteredColumns
      .filter((column) => column.visible)
      .map((column) => column.id)
  }

  /**
   * Returns only the mutable column details from the columns observable.
   */
  @computed
  get mutableSettings(): MutableColumnDetails[] {
    return this.columns.map((column) => {
      return {
        id: column.id,
        visible: column.visible,
      }
    })
  }

  /**
   * Returns value indicating whether all columns' visibility property are true.
   */
  @computed
  get allColumnsVisible() {
    return this.filteredColumns.every((column) => column.visible)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    this._loadColumnSettings()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Indicates whether the user is allowed to select jobs for actions.
   */
  canSelectJobsForActions() {
    const selectedWorkspaces = this._getSelectedWorkspaces()
    const currentMemberships = this._getCurrentUserWorkspaceMemberships()

    return selectedWorkspaces.length === 0
      ? this._getCurrentUserWorkspaceMemberships().every(
          (m) => m.member.permissions.adminJobList,
        )
      : selectedWorkspaces.every(
          (id) =>
            currentMemberships.find((m) => m.workspace.id === id)?.member
              .permissions.adminJobList,
        )
  }

  /**
   * Triggers download of jobs data organized into a CSV file.
   */
  exportJobsAsCSV = task.resolved(() => this._adapter.downloadCsv())

  /**
   * Sets column manager dropdown state to open.
   */
  @action.bound
  openColumnManager() {
    this.columnManagerOpen = true
  }

  /**
   * Sets column manager dropdown state to closed.
   */
  @action.bound
  closeColumnManager() {
    this.columnManagerOpen = false
    this.saveColumnSettings()
  }

  /**
   * Toggles column manager dropdown state.
   */
  @action.bound
  toggleColumnManager() {
    if (this.columnManagerOpen) {
      this.closeColumnManager()
    } else {
      this.openColumnManager()
    }
  }

  /**
   * Saves mutable column settings to local storage.
   */
  @action.bound
  saveColumnSettings() {
    this._storage.setItem(this._settingsKey, this.mutableSettings)
  }

  /**
   * Sets column visibility state to true.
   * @param columnId
   */
  @action.bound
  showColumn(columnId: string) {
    const index = this._findIndex(columnId)
    this.columns[index].visible = true
  }

  /**
   * Sets column visibility state to false, as long as column modify visibility
   * property is true.
   * @param columnId
   */
  @action.bound
  hideColumn(columnId: string) {
    const index = this._findIndex(columnId)
    if (this.columns[index].modifyVisibility) {
      this.columns[index].visible = false
    }
  }

  /**
   * Sets the density of all columns and remembers it.
   */
  @action.bound
  setDensity(density: ColumnDensity) {
    this.density = density
    this._storage.setItem(this._densitySettingKey, this.density)
  }

  /**
   * Toggles column visibility state.
   * @param columnId
   */
  @action.bound
  toggleColumnVisibility(columnId: string) {
    const index = this._findIndex(columnId)
    if (this.columns[index].visible) {
      this.hideColumn(columnId)
    } else {
      this.showColumn(columnId)
    }
  }

  /**
   * Sets all columns' visible property to their opposite states.
   * Determines toggle orientation relative to visibility for all columns.
   * If a single column is not visible, toggle will set all columns visible.
   */
  @action.bound
  toggleAllColumnVisibility() {
    const allVisible = this.allColumnsVisible
    this.columns.forEach((column) => {
      if (allVisible) {
        this.hideColumn(column.id)
      } else {
        this.showColumn(column.id)
      }
    })
  }

  /**
   * Moves a column, given by its position among the filtered columns.
   */
  @action.bound
  updateColumnOrder(fromFilteredIndex: number, toFilteredIndex?: number) {
    if (!isNotNil(toFilteredIndex)) {
      return
    }
    const filteredColumn = this.filteredColumns[fromFilteredIndex]
    const toFilteredColumn = this.filteredColumns[toFilteredIndex]

    const fromIndex = this.columns.findIndex(
      (column) => column.id === filteredColumn.id,
    )
    const toIndex = this.columns.findIndex(
      (column) => column.id === toFilteredColumn.id,
    )

    const column = this.columns[fromIndex]

    this.columns.splice(fromIndex, 1)
    this.columns.splice(toIndex, 0, column)
  }

  private get _settingsKey() {
    return `${this._keyPrefix} ${this._userKey}`
  }

  private get _densitySettingKey() {
    return `${this._keyPrefix} ${this._densityKey}`
  }

  /**
   * Loads mutable column settings from local storage, updating columns observable.
   * Matches columns in local storage to those in JobsTableColumns, and ensures
   * columns observable has all columns, even if local storage does not.
   */
  @action.bound
  private _loadColumnSettings() {
    const columnList = JobsTableColumns
    const settings =
      (this._storage.getItem(this._settingsKey) as MutableColumnDetails[]) ||
      defaultSettings
    const density =
      parseStoredColumnDensity(
        this._storage.getItem(this._densitySettingKey),
      ) ?? 'Default'

    const findColumn = (columnId: string) =>
      columnList.find((col) => (col.id as string) === columnId)

    this.columns = settings
      .map((column) => {
        const found = findColumn(column.id)
        if (!found) {
          return null
        }
        return {
          ...found,
          visible: found.modifyVisibility ? column.visible : true,
        }
      })
      .filter(isNotNil)

    if (this.columns.length < columnList.length) {
      const extraColumns = columnList
        .map((definition) => {
          const isAlreadyInColumnList = this.columns.some(
            (col) => col.id === definition.id,
          )
          if (isAlreadyInColumnList) {
            return null
          }
          return {
            ...definition,
            visible: true,
          }
        })
        .filter(isNotNil)

      this.columns.push(...extraColumns)
    }

    this.density = density

    // Ensure the action column is always pinned to the head of the column list.
    const actionIndex = this.columns.findIndex((col) => col.actionSelector)
    if (actionIndex === -1) {
      return
    }

    move(this.columns, actionIndex, 0)
  }

  /**
   * Returns index of a column using the column id.
   * @param columnId
   * @returns
   */
  private _findIndex(columnId: string) {
    return this.columns.findIndex((col) => (col.id as string) === columnId)
  }

  /**
   * Determines whether a column is accessible to a user.
   */
  private _isColumnAccessible(
    column: StaticColumnDetails & MutableColumnDetails,
    canViewPayout: boolean,
    canViewTotal: boolean,
    canViewIsRequestedPro: boolean,
    canSelectJobsForActions: boolean,
    canViewClientInfo: boolean,
    canViewProInfo: boolean,
  ) {
    switch (column.id) {
      case 'checkbox':
        return canSelectJobsForActions
      case 'jobTitle':
        return true
      case 'jobShortId':
        return true
      case 'legendName':
        return true
      case 'workspace':
        return true
      case 'status':
        return true
      case 'progressionStep':
        return true
      case 'jobScopeChangeRequestStatus':
        return true
      case 'clientTeam':
        return true
      case 'clientChampion':
        return true
      case 'clientChampionEmail':
        return canViewClientInfo
      case 'proChampion':
        return true
      case 'proChampionEmail':
        return canViewProInfo
      case 'isRequestedPro':
        return canViewIsRequestedPro
      case 'payout':
        return canViewPayout
      case 'total':
        return canViewTotal
      case 'totalPaid':
        return false
      case 'created':
        return true
      case 'submitted':
        return true
      case 'claimed':
        return true
      case 'completed':
        return true
      case 'advanceAllowed':
        return FeatureToggles.pendingJobs()
      case 'lastProMessageActivity':
        return true
      case 'lastClientMessageActivity':
        return true
      case 'lastRelationshipManagerMessageActivity':
        return true
      default:
        return false
    }
  }

  /**
   * Determine whether user should be allowed to view client information.
   */
  private _canViewClientInfo() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (m) => m.member.permissions.viewClientDetails,
    )
  }

  /**
   * Determine whether user should be allowed to view pro champion information.
   */
  private _canViewProInfo() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (m) => m.member.permissions.adminProPiView,
    )
  }

  /**
   * Determine whether user should be allowed to view payout column.
   */
  private _canViewPayoutColumn(id: Id) {
    const isUserTypePro = this._getUserData(id)?.userDataType === 'Pro'
    const hasAdminViewPricing = this._getCurrentUserWorkspaceMemberships().some(
      (m) => m.member.permissions.adminProCutView,
    )

    return isUserTypePro || hasAdminViewPricing
  }

  /**
   * Determine whether user should be allowed to view total column.
   */
  private _canViewTotalColumn(id: Id) {
    const isUserTypeUser = this._getUserData(id)?.userDataType === 'User'
    const hasAdminViewPricing = this._getCurrentUserWorkspaceMemberships().some(
      (m) => m.member.permissions.adminViewPricing,
    )

    return isUserTypeUser || hasAdminViewPricing
  }

  /**
   * Determine whether user should be allowed to view is requested pro column.
   */
  private _canViewIsRequestedProColumn() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (m) => m.member.permissions.adminAccess,
    )
  }
}
